import React from 'react';
import { Formik } from 'formik';
import { Col, DatePicker, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Select } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import moment from 'moment';
import { InfoCircleOutlined } from '@ant-design/icons';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import useDatepickerHandler from '../../../../app/hooks/useDatepickerHandler';
import { DATE_FORMAT, statusOptions } from '../../../../const/system';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import { mkValidationSchema } from './FormFiltertemplates.const';



const FormFilterTemplates = ({
  initialValues,
  formErrors = {},
  contentTypes,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const { onBlur } = useDatepickerHandler();

  const contentTypesOptions = contentTypes?.map((contentType) => {
    return {
      label: intl.formatMessage({ id: `structures-source-${contentType.name}` }),
      value: contentType.name,
    };
  });

  const disabledDate = (current) => {
    return current && current > moment();
  };

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={mkValidationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched }) => {
        return (
          <Form layout="vertical" >
            <FormItem
              name='id'
              label={
                <Row className='w-full' justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input
                name="id"
                placeholder={intl.formatMessage({ id: 'templates-filter-form-id-placeholder' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'ui-general-title' })}
              name='title'
            >
              <Input
                name="title"
                placeholder={intl.formatMessage({ id: 'templates-filter-form-title-placeholder' })}
              />
            </FormItem>

            <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.GET_CONTENT_TYPES ]}>
              <FormItem
                label={intl.formatMessage({ id: 'ui-general-content-type' })}
                name='content_type'
              >
                <Select
                  allowClear
                  name="content_type"
                  options={contentTypesOptions}
                  placeholder={intl.formatMessage({ id: 'ui-general-choose-content-type' })}
                />
              </FormItem>
            </PrivateComponent>

            <FormItem
              label={intl.formatMessage({ id: 'ui-general-status' })}
              name='status'
            >
              <Select
                allowClear
                name="status"
                value={values?.status?.toString() ?? null}
                options={statusOptions}
                placeholder={intl.formatMessage({ id: 'ui-general-choose-status' })}
              />
            </FormItem>

            <Row gutter={16}>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-created-from' })}
                  name='created_from'
                >
                  <DatePicker
                    name='created_from'
                    format={DATE_FORMAT}
                    showToday
                    disabledDate={disabledDate}
                    value={values?.created_from ? moment(values.created_from) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('created_from', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('created_from', true);
                      await onBlur('created_from', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem
                  label={intl.formatMessage({ id: 'ui-general-created-to' })}
                  name='created_to'
                >
                  <DatePicker
                    name='created_to'
                    format={DATE_FORMAT}
                    disabledDate={disabledDate}
                    value={values?.created_to ? moment(values.created_to) : null}
                    onChange={async (date, dateString) => {
                      await setFieldValue('created_to', dateString);
                    }}
                    onBlur={async ({ target }) => {
                      await setFieldTouched('created_to', true);
                      await onBlur('created_to', target.value, setFieldValue);
                    }}
                  />
                </FormItem>
              </Col>
            </Row>

            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id='ui-general-reset' />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton
                  type="primary"
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormFilterTemplates.propTypes = {
  initialValues: PropTypes.object.isRequired,
  formErrors: PropTypes.object,
  contentTypes: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormFilterTemplates;
